import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const normalizeText = (t = "") => {
  // Quill siempre agrega \n al final del texto
  return t.replace(/\n$/, "");
};

let __richTextEditorIdCounter = 0;

const RichTextEditor = forwardRef(
  ({ value, onChange, placeholder, height = 300, plainText = false }, ref) => {
    const quillRef = useRef(null);
    const idRef = useRef(`rte-${++__richTextEditorIdCounter}`);

    useImperativeHandle(ref, () => ({
      insertText: (text) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength() - 1;
        quill.insertText(index, text);
        quill.setSelection(index + text.length);
      },
    }));

    const modules = useMemo(() => {
      return plainText
        ? { toolbar: false }
        : {
            toolbar: [
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              ["bold", "italic", "underline", "strike"],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              [{ list: "ordered" }, { list: "bullet" }],
              ["link", "clean"],
            ],
          };
    }, [plainText]);

    const formats = useMemo(() => {
      return plainText
        ? []
        : [
            "header",
            "bold",
            "italic",
            "underline",
            "strike",
            "color",
            "background",
            "align",
            "list",
            "bullet",
            "link",
          ];
    }, [plainText]);

    // ✅ En plainText sincroniza el contenido con setText (evita loop)
    useEffect(() => {
      if (!plainText) return;
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const incoming = normalizeText(value || "");
      const current = normalizeText(quill.getText());

      if (incoming !== current) {
        const sel = quill.getSelection();
        quill.setText(incoming);
        if (sel) quill.setSelection(sel); // opcional: mantener cursor
      }
    }, [value, plainText]);

    const handleChange = (content, delta, source, editor) => {
      // 🔒 clave: solo reaccionar a cambios del usuario
      if (source !== "user") return;

      if (plainText) {
        const text = normalizeText(editor.getText());
        // evitar setState inútil si no cambió
        if (text !== normalizeText(value || "")) onChange?.(text);
      } else {
        onChange?.(content);
      }
    };

    return (
      <div className={`text-editor ${idRef.current}`}>
        <style>{`
          .${idRef.current} .ql-container {
            height: ${height}px;
          }
          .${idRef.current} .ql-editor {
            min-height: ${height}px;
            box-sizing: border-box;
          }
        `}</style>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          // ✅ En plainText no lo controles con `value` para no disparar loops
          {...(plainText ? {} : { value: value || "" })}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder || "Escribe aquí..."}
        />
      </div>
    );
  }
);

export default RichTextEditor;